import { OwnerShare } from 'src/financial/financial-data/ownerShares';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import { GroupMember } from '../grouping';
import { Expenditure } from '../hooks/useExpenditures';
import { Income } from '../hooks/useIncomes';
import { LifeEvent } from '../hooks/useLifeEvents';
import { ExpenditureRow, IncomeRow, LifeEventRow, OwnerPart } from '../rows';
import { CashflowOwner, IncomeOwner, ResolvedShare } from './ownerTypes';

/**
 * Who an item is held by, for the "Family owners" column and the deletion warning: its client
 * owners as the family knows them, and anyone named who is not a client.
 */
function ownerMembers(
  owners: CashflowOwner[] | undefined,
  memberByClientId: Map<string, GroupMember>,
): GroupMember[] {
  return (owners ?? []).map(owner => {
    if (owner.clientId) {
      return (
        memberByClientId.get(owner.clientId) ?? {
          clientId: owner.clientId,
          firstName: 'Unknown member',
          lastName: '',
        }
      );
    }
    return {
      clientId: `external:${owner.externalName}`,
      firstName: owner.externalName ?? '',
      lastName: '',
    };
  });
}

/**
 * An item's owners. One read from an API that predates owners names none, so it is held by the
 * client it was read through, alone and with no share recorded.
 */
export function ownersOf(item: { clientId: string; owners?: CashflowOwner[] }): CashflowOwner[] {
  return (
    item.owners ?? [{ clientId: item.clientId, externalName: null, ownershipPercentage: null }]
  );
}

export function incomeOwnersOf(income: Income): IncomeOwner[] {
  return (
    income.owners ?? [
      {
        clientId: income.clientId,
        externalName: null,
        ownershipPercentage: null,
        amountPostTax: income.amountPostTax,
      },
    ]
  );
}

/** Owners named by someone other than a client, which the tab keeps as they are on a save. */
export function externalOwners<T extends CashflowOwner>(owners: T[] | undefined): T[] {
  return (owners ?? []).filter(owner => !owner.clientId);
}

/**
 * Whose part of each item the totals count: one client's, or the household's, which is every
 * family member's part together. A client from outside the family is counted on no tab here, as
 * the household's cashflow model leaves them out, and neither is anyone named who is not a client.
 */
type CountedFor = { clientId: string } | { household: Map<string, GroupMember> };

function countsFor(owner: CashflowOwner, countedFor: CountedFor): boolean {
  if (!owner.clientId) return false;
  return 'household' in countedFor
    ? countedFor.household.has(owner.clientId)
    : owner.clientId === countedFor.clientId;
}

/** A percentage of an amount, to the penny, as an owner's part of an item is always counted. */
export function shareInPounds(amount: number, percentage: number): number {
  return Math.round(amount * percentage) / 100;
}

/** The share an owner without a recorded one has: all of an item held alone, else an even split. */
function unrecordedShare(ownerCount: number): ResolvedShare {
  return ownerCount <= 1
    ? { percentage: 100, isAssumed: false }
    : { percentage: 100 / ownerCount, isAssumed: true };
}

/**
 * An owner's share as the API resolved it. An API that predates resolved shares sends none, so
 * the share is the one an unrecorded owner has.
 */
function resolvedShareOf(owner: CashflowOwner, owners: CashflowOwner[]): ResolvedShare {
  return owner.resolvedShare ?? unrecordedShare(owners.length);
}

/** Some owners have a share and some do not: a set still being typed, which no save accepts. */
export function isPartlyRecorded(owners: CashflowOwner[]): boolean {
  const recorded = owners.filter(owner => owner.ownershipPercentage != null).length;
  return recorded > 0 && recorded < owners.length;
}

/**
 * Each owner's share of an item as it stands in a form, which the API has not resolved yet, by
 * the backend's own rule (`resolved_shares`): recorded shares as they stand when every owner has
 * one, otherwise the share an unrecorded owner has, for everyone.
 */
export function sharesAsRecorded(owners: CashflowOwner[]): ResolvedShare[] {
  if (owners.length > 0 && owners.every(owner => owner.ownershipPercentage != null)) {
    return owners.map(owner => ({ percentage: owner.ownershipPercentage ?? 0, isAssumed: false }));
  }
  return owners.map(() => unrecordedShare(owners.length));
}

/** The part of an item's amount its counted owners hold between them. */
function countedPart(
  amount: number | null,
  owners: CashflowOwner[],
  countedFor: CountedFor,
): number | null {
  if (amount == null) return null;
  const percentage = owners
    .filter(owner => countsFor(owner, countedFor))
    .reduce((total, owner) => total + resolvedShareOf(owner, owners).percentage, 0);
  // To the penny: a share the API resolves to 33.333333% would otherwise leave a third of 900
  // pounds as 299.999997, and totals of whole pounds would show pence
  return shareInPounds(amount, percentage);
}

/**
 * The share the Ownership column shows on one client's tab, as the API resolved it. The family tab
 * names the owners instead, so it has none, and a client the item does not name holds none of it.
 */
function ownerShareOf(owners: CashflowOwner[], countedFor: CountedFor): OwnerShare | undefined {
  if (!('clientId' in countedFor)) return undefined;
  const mine = owners.find(owner => owner.clientId === countedFor.clientId);
  return mine ? resolvedShareOf(mine, owners) : { percentage: 0, isAssumed: false };
}

/**
 * What each owner of a shared item holds, as the API resolved their shares, so the parts are the
 * figures each member's own tab counts. Someone named who is not a client has a part too: it is
 * still not the family's. An item held alone has no parts to tell apart.
 */
function ownerPartsOf<T extends CashflowOwner>(
  owners: T[],
  memberByClientId: Map<string, GroupMember>,
  amount: number | null,
  postTaxOf?: (owner: T) => number | null,
): OwnerPart[] | undefined {
  if (owners.length < 2) return undefined;
  return owners.map((owner, index) => {
    const share = resolvedShareOf(owner, owners);
    const member = ownerMembers([owner], memberByClientId)[0];
    return {
      name:
        formatName(member.firstName, member.lastName, NameFormat.NATURAL) || `Owner ${index + 1}`,
      share,
      amount: amount == null ? null : shareInPounds(amount, share.percentage),
      ...(postTaxOf && { amountPostTax: postTaxOf(owner) }),
    };
  });
}

/** The family tab, where no single member is "me", shows each owner's part instead of one share. */
function isFamilyView(countedFor: CountedFor | undefined): boolean {
  return !countedFor || 'household' in countedFor;
}

/**
 * A joint item is read once through each member who holds it, so a family-wide read carries it
 * once per member. The first copy stands for the item.
 */
function onceEach<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter(item => !seen.has(item.id) && seen.add(item.id));
}

function sumOwners(owners: IncomeOwner[] | undefined): number | null {
  const recorded = (owners ?? [])
    .map(owner => owner.amountPostTax)
    .filter((amount): amount is number => amount != null);
  return recorded.length === 0 ? null : recorded.reduce((total, amount) => total + amount, 0);
}

/** Names the client owners whose after-tax amount is missing, when only some have one. */
function partialPostTaxNote(
  owners: IncomeOwner[],
  memberByClientId: Map<string, GroupMember>,
): string | undefined {
  const clientOwners = owners.filter(owner => owner.clientId);
  const missing = clientOwners.filter(owner => owner.amountPostTax == null);
  if (missing.length === 0 || missing.length === clientOwners.length) return undefined;
  const namesOf = (subset: IncomeOwner[]) =>
    ownerMembers(subset, memberByClientId)
      .map(member => formatName(member.firstName, member.lastName, NameFormat.NATURAL))
      .join(', ');
  return `Not recorded for ${namesOf(missing)}. This figure includes ${namesOf(
    clientOwners.filter(owner => owner.amountPostTax != null),
  )} only.`;
}

/**
 * Each row is the whole item. An income's after-tax figure is its owners' own amounts added up,
 * since each owner is taxed on their share. What it adds to the totals is the counted owners'
 * part before tax and their own amounts after it.
 */
export function buildJointIncomeRows(
  incomes: Income[],
  memberByClientId: Map<string, GroupMember>,
  countedFor?: CountedFor,
): IncomeRow[] {
  return onceEach(incomes).map(income => ({
    id: income.id,
    members: ownerMembers(incomeOwnersOf(income), memberByClientId),
    incomeType: income.incomeType,
    description: income.description,
    frequency: income.frequency,
    amountPreTax: income.amountPreTax,
    amountPostTax: sumOwners(incomeOwnersOf(income)),
    items: [income],
    postTaxNote: partialPostTaxNote(incomeOwnersOf(income), memberByClientId),
    ...(countedFor && {
      counted: {
        amountPreTax: countedPart(income.amountPreTax, incomeOwnersOf(income), countedFor),
        amountPostTax: sumOwners(
          incomeOwnersOf(income).filter(owner => countsFor(owner, countedFor)),
        ),
      },
      ownerShare: ownerShareOf(incomeOwnersOf(income), countedFor),
    }),
    ...(isFamilyView(countedFor) && {
      ownerParts: ownerPartsOf(
        incomeOwnersOf(income),
        memberByClientId,
        income.amountPreTax,
        owner => owner.amountPostTax,
      ),
    }),
  }));
}

export function buildJointExpenditureRows(
  expenditures: Expenditure[],
  memberByClientId: Map<string, GroupMember>,
  countedFor?: CountedFor,
): ExpenditureRow[] {
  return onceEach(expenditures).map(expenditure => ({
    id: expenditure.id,
    members: ownerMembers(ownersOf(expenditure), memberByClientId),
    category: expenditure.category,
    description: expenditure.description,
    frequency: expenditure.frequency,
    amount: expenditure.amount,
    items: [expenditure],
    ...(countedFor && {
      counted: { amount: countedPart(expenditure.amount, ownersOf(expenditure), countedFor) },
      ownerShare: ownerShareOf(ownersOf(expenditure), countedFor),
    }),
    ...(isFamilyView(countedFor) && {
      ownerParts: ownerPartsOf(ownersOf(expenditure), memberByClientId, expenditure.amount),
    }),
  }));
}

export function buildJointLifeEventRows(
  lifeEvents: LifeEvent[],
  memberByClientId: Map<string, GroupMember>,
  countedFor?: CountedFor,
): LifeEventRow[] {
  return onceEach(lifeEvents).map(lifeEvent => ({
    id: lifeEvent.id,
    members: ownerMembers(ownersOf(lifeEvent), memberByClientId),
    description: String(lifeEvent.description ?? ''),
    eventDate: lifeEvent.eventDate,
    details: lifeEvent.details,
    cost: lifeEvent.cost,
    items: [lifeEvent],
    ...(countedFor && {
      counted: { cost: countedPart(lifeEvent.cost, ownersOf(lifeEvent), countedFor) },
      ownerShare: ownerShareOf(ownersOf(lifeEvent), countedFor),
    }),
    ...(isFamilyView(countedFor) && {
      ownerParts: ownerPartsOf(ownersOf(lifeEvent), memberByClientId, lifeEvent.cost),
    }),
  }));
}
